// Process trail — the chain on the left, the document on the right.
//
// A trail belongs to a record, so it opens from one (the row menu beside Audit
// trail, or an open record's overflow) and never from the list toolbar, where
// nothing is selected yet. Choosing a record on the left opens that transaction
// on the right IN PLACE: a jump that unloaded the chain is what made "open the
// linked transaction" feel like leaving.
//
// **Two ways to read one chain, and the reader picks.** The DESIGN view draws
// the process as its author drew it, with each step's records inside its box —
// this is the picture people already know from the Process Flow designer. The
// TREE view nests a record inside the record it came from, which is the better
// answer to "what contains what" and stays the right choice for a deep chain.
// Neither is a superset of the other, so the choice is remembered per person.
//
// **A trail can move.** A record further down the chain has a chain of its own
// — its siblings, what it produced, what else it touched — so "Process trail"
// on any record re-anchors this window on that record, with a Back that returns
// to where the reader came from. Opening a second window on top of the first
// would have made "close" ambiguous, and the trail is a place, not a stack of
// dialogs.
//
// **What may be DONE to a record is the host's answer for its own object, and
// the record's own object's answer for everything else.** The screen that
// opened the trail already implements its object's actions, rights-gated, so
// those are threaded in. A record of ANOTHER object resolves its own action
// buttons from its model and its own per-object rights, and runs them through
// the SAME confirmation and warning gates the object's screen would — an action
// nobody may run is never offered, and one that needs an open form says so
// rather than half-running.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ExternalLink, Play, Waypoints, X } from 'lucide-react'

import type { ActionDef, SqlModel, TransactionModel } from '../types'
import type { RunActionResponse } from '../contracts'
import FormRenderer, { FormModeBadge } from '../FormRenderer'
import { parseApiError, visibleRowActions } from '../utils'
import { attemptWithWarningConfirm } from '../warnings'
import { useWarningConfirm } from '../WarningsConfirmModal'
import {
  useActionConfirm, attemptActionWithConfirm, isActionConfirmCancelled,
} from '../ActionConfirmModal'
import { canUseButton } from '../screen/contract'
import type { ObjectRightsSummary } from '../screen/contract'
import RowActionsMenu, { MENU_Z_OVER_MODAL, type RowAction } from '../list/RowActionsMenu'
import { Z_TRAIL_WINDOW } from '../../layers'
import { processCardBody, processTrail } from './contract'
import type { FeedBody, NodeActions, TrailNode, TrailResponse } from './contract'
import { buildRecordActions, splitRecordActions } from './recordActions'
import TrailDesign from './TrailDesign'
import TrailTree from './TrailTree'
import { anchorPath, bodyNodes, branchKeys, findAnchor, nodeKey } from './trailNodes'

export interface TrailRuntime {
  execute(objName: string, action: string, payload?: Record<string, unknown>): Promise<unknown>
  /** The object's model, for the right-hand pane — and for the action buttons
   *  of an object this window's host does not own. Optional: without it the
   *  pane offers the card and a link out instead of the form. */
  getModel?(objName: string): Promise<TransactionModel>
  /** One record's header values. Reuses the screen's own load. */
  load(objName: string, txnId: string): Promise<{ header?: Record<string, unknown> }>
  /** Per-object end-user rights, where the host resolves them (the standalone
   *  runtime does; the designer preview does not, and means full rights —
   *  the server gates either way). */
  rights?(objName: string): Promise<ObjectRightsSummary>
}

/** Which of the two readings this person last used. A view is a habit, not a
 *  setting: asking again every time is the thing to avoid. */
const VIEW_MEMORY = 'twasta.trail.view'
type TrailView = 'design' | 'tree'

function rememberedTrailView(): TrailView {
  try {
    return window.localStorage.getItem(VIEW_MEMORY) === 'tree' ? 'tree' : 'design'
  } catch {
    return 'design'
  }
}

/** How wide the chain is, in px. A habit like the view is: the record's form is
 *  the transaction editor's own, and that form lays itself out in as many
 *  columns as the width allows — so where this divider sits decides whether a
 *  reader sees one column or three, and asking them to set it every time is the
 *  thing to avoid. */
const SPLIT_MEMORY = 'twasta.trail.split'
const SPLIT_DEFAULT = 560
const SPLIT_MIN = 320
/** Below this the form has no room to be a form. */
const PANE_MIN = 340

function rememberedSplit(): number {
  try {
    const n = Number(window.localStorage.getItem(SPLIT_MEMORY))
    return Number.isFinite(n) && n >= SPLIT_MIN ? n : SPLIT_DEFAULT
  } catch {
    return SPLIT_DEFAULT
  }
}

interface Props {
  project: string
  objName: string
  txnId: string
  runtime: TrailRuntime
  onClose: () => void
  /** Take the viewer to this record on its own screen. Optional — the pane
   *  shows the record either way; this is the way OUT of the trail. */
  onOpenFullScreen?: (objName: string, txnId: string) => void
  /** What the host lets the viewer DO to a record of ITS object. */
  nodeActions?: NodeActions
  /** Called after an action or a delete ran, so the host re-reads its list. */
  onChanged?: () => void
}

export default function ProcessTrailModal({
  project, objName, txnId, runtime, onClose, onOpenFullScreen, nodeActions, onChanged,
}: Props) {
  const [data, setData] = useState<TrailResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flowName, setFlowName] = useState<string | undefined>(undefined)
  const [selected, setSelected] = useState<TrailNode | null>(null)
  const [view, setView] = useState<TrailView>(rememberedTrailView)
  const [leftW, setLeftW] = useState<number>(rememberedSplit)
  const [dragging, setDragging] = useState(false)
  // Where the trail is anchored NOW, and how the reader got here.
  const [anchor, setAnchor] = useState({ obj: objName, txnId })
  const [came, setCame] = useState<Array<{ obj: string; txnId: string }>>([])
  const [reload, setReload] = useState(0)
  // What an action just said. Shown in the window rather than as a toast: the
  // reader is looking here, and a modal over a modal is worse.
  const [note, setNote] = useState<{ bad?: boolean; text: string } | null>(null)
  // Tree view only: which branches are shut, and which card bodies are open.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [bodies, setBodies] = useState<
    Record<string, { open: boolean; busy?: boolean; body?: FeedBody | null }>>({})
  // Objects this window does NOT own: their action buttons and rights.
  const [foreign, setForeign] = useState<Record<string, {
    formNo: string; actions: ActionDef[]; rights: ObjectRightsSummary | null
  }>>({})

  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const { confirmAction, actionConfirmModal } = useActionConfirm()

  // ── the chain ───────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    processTrail(runtime.execute, anchor.obj, anchor.txnId, flowName)
      .then((res) => {
        if (cancelled) return
        setData(res)
        // Open on the record the person came from: hunting for it in a chain
        // they just asked about is the one thing this screen must not do.
        const at = findAnchor(res.roots || [])
        setSelected(at)
        const path = new Set(anchorPath(res.roots || []))
        const shut: Record<string, boolean> = {}
        for (const key of branchKeys(res.roots || [])) {
          if (!path.has(key)) shut[key] = true
        }
        setCollapsed(shut)
        setBodies({})
      })
      .catch((e) => { if (!cancelled) setError(parseApiError(e).message) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, anchor.obj, anchor.txnId, flowName, runtime, reload])

  const switchView = (v: TrailView) => {
    setView(v)
    try { window.localStorage.setItem(VIEW_MEMORY, v) } catch { /* not remembered */ }
  }

  /** Re-anchor on another record — its own chain, in this window. */
  const traceFrom = useCallback((node: TrailNode) => {
    if (!node.txn_id) return
    setCame((c) => [...c, anchor])
    setAnchor({ obj: node.obj_name, txnId: node.txn_id })
    setNote(null)
  }, [anchor])

  const goBack = useCallback(() => {
    setCame((c) => {
      const prev = c[c.length - 1]
      if (prev) setAnchor(prev)
      return c.slice(0, -1)
    })
    setNote(null)
  }, [])

  // An action or a delete changes what the chain says, so it is re-traced once
  // the host's handler settles — and the host re-reads its own list.
  const settled = useCallback(() => {
    setReload((n) => n + 1)
    onChanged?.()
  }, [onChanged])

  // ── objects this window does not own ────────────────────────────────────
  // Their buttons come from their own model, and are filtered by their own
  // per-object rights — the same two answers their screen uses.
  const objectsInTrail = useMemo(() => {
    const out = new Set<string>()
    const walk = (nodes: TrailNode[]) => {
      for (const n of nodes) {
        if (n.txn_id && n.obj_name !== nodeActions?.objName) out.add(n.obj_name)
        walk(n.children || [])
      }
    }
    walk(data?.roots || [])
    return [...out]
  }, [data, nodeActions])

  const loadedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    let cancelled = false
    const getModel = runtime.getModel
    if (!getModel) return
    for (const obj of objectsInTrail) {
      if (loadedRef.current.has(obj)) continue
      loadedRef.current.add(obj)
      void Promise.all([
        getModel(obj),
        runtime.rights ? runtime.rights(obj).catch(() => null) : Promise.resolve(null),
      ]).then(([model, rights]) => {
        if (cancelled) return
        const sm = model?.sql_models?.[0]?.sql_model
        // Row-scoped buttons only, and none of the BUILT-IN ops (Edit, View,
        // Delete): a system action performs its work through the object's own
        // editor, which this window is not. Running one here would validate,
        // report "done" and change nothing. They stay where they work — on the
        // record's own screen, which "Open full screen" reaches.
        const acts = ((sm?.actions || []) as ActionDef[])
          .filter((a) => String(a.scope || 'row') === 'row'
            && String(a.handler || '') !== 'system')
        setForeign((f) => ({
          ...f,
          [obj]: { formNo: String(sm?.form_no || '1'), actions: acts, rights },
        }))
      }).catch(() => { /* an object we cannot read offers no buttons */ })
    }
    return () => { cancelled = true }
  }, [objectsInTrail, runtime])

  /** Run an action on a record of an object this window does not own.
   *
   *  Through the SAME gates the object's own screen uses — the confirmation
   *  dialog and the acknowledgeable warnings are shared components, not a copy.
   *  Outcomes that need a form open (an item_change, a select grid, a report,
   *  the Assistant) cannot happen in a window with no form: they say so and
   *  point at the record's own screen instead of half-running. */
  const runForeign = useCallback(async (
    obj: string, txn: string, name: string, formNo: string,
  ) => {
    setNote(null)
    try {
      const res = await attemptActionWithConfirm(
        (confirm) => attemptWithWarningConfirm(
          (ignore) => runtime.execute(obj, 'run_action', {
            form_no: formNo,
            action_name: name,
            header: {},
            header_values: {},
            txn_id: txn,
            ...(ignore.length ? { ignore_warnings: ignore } : {}),
            ...(confirm ? { confirm } : {}),
          }),
          confirmWarnings,
        ) as Promise<RunActionResponse>,
        confirmAction,
      )
      const handler = String(res.handler || '')
      if (handler === 'message' || handler === 'system' || !handler) {
        setNote({ text: res.message || `${name} done` })
      } else {
        setNote({
          text: `“${name}” continues on the ${obj} screen — open the record there.`,
        })
      }
      settled()
    } catch (err) {
      if (isActionConfirmCancelled(err)) return
      const parsed = parseApiError(err)
      setNote({
        bad: true,
        text: parsed.validationErrors[0]?.message || parsed.message || `${name} failed`,
      })
    }
  }, [runtime, confirmWarnings, confirmAction, settled])

  /** One record's menu. The host's own object gets the host's handlers; every
   *  other object gets its own buttons, its own rights, and the ways out. */
  const actionsFor = useCallback((node: TrailNode): RowAction[] => {
    if (!node.txn_id) return []
    const id = node.txn_id
    const isHere = node.obj_name === anchor.obj && id === anchor.txnId
    const trail = isHere ? undefined : () => traceFrom(node)
    const out = onOpenFullScreen ? () => onOpenFullScreen(node.obj_name, id) : undefined
    if (nodeActions && nodeActions.objName === node.obj_name) {
      return buildRecordActions({
        txnId: id,
        editable: node.editable,
        hiddenActions: node.hidden_actions,
        own: nodeActions,
        onRun: (name) => {
          void Promise.resolve(nodeActions.onRunRowAction?.(name, id)).finally(settled)
        },
        onDelete: () => {
          void Promise.resolve(nodeActions.onDelete?.(id)).finally(settled)
        },
        onProcessTrail: trail,
        onOpenFullScreen: out,
      })
    }
    // Another object's record.
    const info = foreign[node.obj_name]
    const acts: RowAction[] = []
    for (const a of visibleRowActions(info?.actions, node.hidden_actions)) {
      if (!canUseButton(info?.rights, a.name)) continue
      acts.push({
        label: a.name,
        icon: <Play size={14} />,
        onClick: () => { void runForeign(node.obj_name, id, a.name, info.formNo) },
      })
    }
    if (out) {
      acts.push({ label: 'Open full screen', icon: <ExternalLink size={14} />, onClick: out })
    }
    if (trail) {
      acts.push({ label: 'Process trail', icon: <Waypoints size={14} />, onClick: trail })
    }
    return acts
  }, [anchor, nodeActions, foreign, onOpenFullScreen, traceFrom, runForeign, settled])

  /** Which of the object's own action BUTTONS the viewer may run on a record —
   *  the same two answers the kebab is built from, handed to the record pane's
   *  form so the pane offers exactly what the object's own screen would. */
  const canUseRecordAction = useCallback((node: TrailNode) => {
    const hidden = node.hidden_actions
    if (nodeActions && nodeActions.objName === node.obj_name) {
      const ok = new Set(
        visibleRowActions(nodeActions.rowActions, hidden).map((a) => a.name))
      return (name: string) => ok.has(name)
    }
    const info = foreign[node.obj_name]
    const ok = new Set(
      visibleRowActions(info?.actions, hidden)
        .filter((a) => canUseButton(info?.rights, a.name))
        .map((a) => a.name))
    return (name: string) => ok.has(name)
  }, [nodeActions, foreign])

  // ── tree view: a card's body, fetched when it is opened ─────────────────
  const openBody = useCallback(async (node: TrailNode) => {
    const key = nodeKey(node)
    if (!node.txn_id) return
    setBodies((b) => ({ ...b, [key]: { open: true, busy: !b[key]?.body, body: b[key]?.body } }))
    if (bodies[key]?.body) return
    try {
      const body = await processCardBody(runtime.execute, node.obj_name, node.txn_id)
      setBodies((b) => ({ ...b, [key]: { open: true, busy: false, body } }))
    } catch (e) {
      setBodies((b) => ({ ...b, [key]: { open: true, busy: false, body: null } }))
      console.warn('trail card body failed', e)
    }
  }, [runtime, bodies])

  const toggleBody = useCallback((node: TrailNode) => {
    const key = nodeKey(node)
    if (bodies[key]?.open) {
      setBodies((b) => ({ ...b, [key]: { ...b[key], open: false } }))
      return
    }
    void openBody(node)
  }, [bodies, openBody])

  /** Open or close many bodies at once — a step's expand-all, and the window's.
   *  Bulk rather than a loop of toggles: half the set open and half shut would
   *  make one button mean two different things. */
  const setBodiesOpen = useCallback(async (nodes: TrailNode[], open: boolean) => {
    const want = nodes.filter((n) => !!n.txn_id)
    if (!want.length) return
    if (!open) {
      setBodies((b) => {
        const next = { ...b }
        for (const n of want) {
          const k = nodeKey(n)
          if (next[k]) next[k] = { ...next[k], open: false }
        }
        return next
      })
      return
    }
    setBodies((b) => {
      const next = { ...b }
      for (const n of want) {
        const k = nodeKey(n)
        next[k] = { open: true, busy: !next[k]?.body, body: next[k]?.body }
      }
      return next
    })
    await Promise.all(want.map(async (n) => {
      const k = nodeKey(n)
      if (bodies[k]?.body) return
      try {
        const body = await processCardBody(runtime.execute, n.obj_name, n.txn_id as string)
        setBodies((b) => ({ ...b, [k]: { open: true, busy: false, body } }))
      } catch (e) {
        setBodies((b) => ({ ...b, [k]: { open: true, busy: false, body: null } }))
        console.warn('trail card body failed', e)
      }
    }))
  }, [runtime, bodies])

  const toggleBranch = useCallback((key: string) => {
    setCollapsed((c) => ({ ...c, [key]: !c[key] }))
  }, [])
  // "Everything open" means the branches in the tree and the record details on
  // the diagram — the same button, answering what the reader is looking at.
  const expandAll = useCallback(() => {
    if (view === 'tree') { setCollapsed({}); return }
    void setBodiesOpen(bodyNodes(data?.roots || []), true)
  }, [view, data, setBodiesOpen])
  const collapseAll = useCallback(() => {
    if (view === 'tree') {
      const shut: Record<string, boolean> = {}
      for (const key of branchKeys(data?.roots || [])) shut[key] = true
      setCollapsed(shut)
      return
    }
    void setBodiesOpen(bodyNodes(data?.roots || []), false)
  }, [view, data, setBodiesOpen])

  /** Drag the divider. The width is kept while dragging and written down once,
   *  on release — a localStorage write per mousemove is a write per pixel. */
  const startDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const x0 = e.clientX
    const w0 = leftW
    let w = leftW
    setDragging(true)
    const move = (ev: MouseEvent) => {
      w = Math.max(SPLIT_MIN, w0 + ev.clientX - x0)
      setLeftW(w)
    }
    const up = () => {
      setDragging(false)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      try { window.localStorage.setItem(SPLIT_MEMORY, String(Math.round(w))) } catch { /* not remembered */ }
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }, [leftW])

  const flows = data?.flows || []
  const flow = useMemo(
    () => flows.find((f) => f.name === data?.flow?.name) || null,
    [flows, data])

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // BELOW the record editor (see `layers`): every action offered here —
        // Edit, Assign, Audit trail — opens a window of its own, and a window
        // opened from inside this one has to land in front of it.
        zIndex: Z_TRAIL_WINDOW,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg, #fff)', color: 'var(--color-text)',
          borderRadius: 12, width: 'min(1180px, 94vw)', height: 'min(760px, 90vh)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        {/* header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary, #fff)',
        }}>
          {came.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={goBack}
              title="Back to the trail you came from"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ArrowLeft size={14} /> Back
            </button>
          )}
          <strong style={{ fontSize: 15 }}>Process trail</strong>
          {data?.flow && (
            <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>
              · {data.flow.title}
            </span>
          )}
          {flows.length > 1 && (
            <select
              value={flowName || data?.flow?.name || ''}
              onChange={(e) => setFlowName(e.target.value)}
              style={{
                fontSize: 13, padding: '2px 6px', borderRadius: 6,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)', color: 'var(--color-text)',
              }}
            >
              {flows.map((f) => <option key={f.name} value={f.name}>{f.title}</option>)}
            </select>
          )}
          <span style={{ flex: 1 }} />
          <button className="btn btn-ghost btn-sm" onClick={expandAll}
            title={view === 'tree' ? 'Open every branch' : 'Open every record’s detail'}>
            Expand all
          </button>
          <button className="btn btn-ghost btn-sm" onClick={collapseAll}
            title={view === 'tree' ? 'Shut every branch' : 'Close every record’s detail'}>
            Collapse all
          </button>
          <ViewToggle value={view} onChange={switchView} />
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <X size={15} />
          </button>
        </div>

        {note && (
          <div style={{
            padding: '7px 14px', fontSize: 13.5,
            borderBottom: '1px solid var(--color-border)',
            color: note.bad ? 'var(--color-error, #dc2626)' : 'var(--color-text)',
            background: note.bad
              ? 'var(--color-error-weak, rgba(207,34,46,0.10))'
              : 'var(--color-accent-weak, rgba(0,97,208,0.07))',
          }}>
            {note.text}
          </div>
        )}

        {/* body: chain | document */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <div style={{
            width: leftW, minWidth: SPLIT_MIN, maxWidth: `calc(100% - ${PANE_MIN}px)`,
            flex: 'none', minHeight: 0, display: 'flex', flexDirection: 'column',
          }}>
            {error && (
              <div style={{ color: 'var(--color-error, #dc2626)', fontSize: 14, padding: 14 }}>
                {error}
              </div>
            )}
            {!error && !data && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, padding: 14 }}>
                Tracing this record…
              </div>
            )}
            {data && !data.flow && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, padding: 14 }}>
                This object isn’t placed on a process flow yet.
              </div>
            )}
            {data?.roots && data.roots.length > 0 && view === 'design' && (
              <TrailDesign
                flow={flow}
                roots={data.roots}
                selectedKey={selected ? nodeKey(selected) : undefined}
                onSelect={setSelected}
                bodies={bodies}
                onToggleBody={toggleBody}
                onSetBodies={(nodes, open) => { void setBodiesOpen(nodes, open) }}
                actionsFor={actionsFor}
              />
            )}
            {data?.roots && data.roots.length > 0 && view === 'tree' && (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 12 }}>
                <TrailTree
                  nodes={data.roots}
                  collapsed={collapsed}
                  onToggleBranch={toggleBranch}
                  bodies={bodies}
                  onToggleBody={toggleBody}
                  selectedKey={selected ? nodeKey(selected) : undefined}
                  onOpen={setSelected}
                  actionsFor={actionsFor}
                />
              </div>
            )}
            {data?.truncated && (
              <div style={{
                fontSize: 13, color: 'var(--color-text-secondary)',
                padding: '6px 14px', borderTop: '1px solid var(--color-border)',
              }}>
                This chain is longer than the trail shows. Open a record’s own trail
                to carry on from there.
              </div>
            )}
          </div>

          {/* the divider, which is also the form's column count — see SPLIT_MEMORY */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the chain and the record"
            onMouseDown={startDrag}
            style={{
              flex: 'none', width: 7, cursor: 'col-resize',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderLeft: '1px solid var(--color-border)',
              borderRight: '1px solid var(--color-border)',
              background: dragging
                ? 'var(--color-accent-weak, rgba(0,97,208,0.10))'
                : 'var(--color-bg-secondary, #fff)',
            }}
          >
            <span style={{
              width: 3, height: 34, borderRadius: 999,
              background: dragging
                ? 'var(--color-accent, #0061D0)'
                : 'var(--color-border-strong, #c7cfd9)',
            }} />
          </div>

          <div style={{
            flex: 1, minWidth: PANE_MIN, overflow: 'auto',
            // The record editor's own surface, because this IS the record
            // editor: a pane on a different background reads as a preview of
            // the form rather than the form.
            background: 'var(--color-bg)',
          }}>
            <RecordPane
              project={project}
              node={selected}
              runtime={runtime}
              onOpenFullScreen={onOpenFullScreen}
              actions={selected ? actionsFor(selected) : []}
              canUseAction={selected ? canUseRecordAction(selected) : undefined}
              onActionNote={setNote}
              onActionSettled={settled}
            />
          </div>
        </div>
      </div>

      {warningsModal}
      {actionConfirmModal}
    </div>
  )
}

/** Design or tree — the same chain, read two ways. */
function ViewToggle({ value, onChange }: {
  value: TrailView
  onChange: (v: TrailView) => void
}) {
  const chip = (active: boolean): React.CSSProperties => ({
    fontSize: 13, padding: '3px 9px', cursor: 'pointer', border: 0,
    background: active ? 'var(--color-accent, #0061D0)' : 'transparent',
    color: active ? '#fff' : 'var(--color-text-secondary)',
    fontWeight: active ? 600 : 400,
  })
  return (
    <div style={{
      display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
      border: '1px solid var(--color-border)',
    }}>
      <button type="button" style={chip(value === 'design')}
        title="The process as it was drawn" onClick={() => onChange('design')}>
        Design
      </button>
      <button type="button" style={chip(value === 'tree')}
        title="What came from what" onClick={() => onChange('tree')}>
        Tree
      </button>
    </div>
  )
}

// ── the right-hand pane ─────────────────────────────────────────────────────

function RecordPane({
  project, node, runtime, onOpenFullScreen, actions, canUseAction,
  onActionNote, onActionSettled,
}: {
  project: string
  node: TrailNode | null
  runtime: TrailRuntime
  onOpenFullScreen?: (objName: string, txnId: string) => void
  /** Everything the viewer may do to THIS record, already built and gated. */
  actions: RowAction[]
  /** Which of the object's action buttons this viewer may run on this record. */
  canUseAction?: (name: string) => boolean
  /** Say what an action said, in the window rather than as a toast over it. */
  onActionNote: (note: { bad?: boolean; text: string } | null) => void
  /** Re-trace the chain once an action has settled. */
  onActionSettled: () => void
}) {
  const [model, setModel] = useState<TransactionModel | null>(null)
  const [values, setValues] = useState<Record<string, unknown> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const objName = node?.obj_name
  const txnId = node?.txn_id

  useEffect(() => {
    let cancelled = false
    setModel(null); setValues(null); setError(null)
    if (!objName || !txnId) return
    const wantModel = runtime.getModel
      ? runtime.getModel(objName)
      : Promise.resolve(null as TransactionModel | null)
    Promise.all([wantModel, runtime.load(objName, txnId)])
      .then(([m, rec]) => {
        if (cancelled) return
        setModel(m)
        setValues(rec.header || {})
      })
      .catch((e) => { if (!cancelled) setError(parseApiError(e).message) })
    return () => { cancelled = true }
  }, [objName, txnId, runtime])

  const headerForm: SqlModel | null = useMemo(() => {
    const sm = model?.sql_models?.[0]?.sql_model
    return sm || null
  }, [model])

  // Edit and Open full screen are drawn as buttons; the rest stays in the
  // kebab. A pane this wide had room for the thing people came here to do.
  const { primary, menu } = useMemo(() => splitRecordActions(actions), [actions])

  if (!node) {
    return (
      <div style={{ padding: 20, fontSize: 14, color: 'var(--color-text-secondary)' }}>
        Choose a step on the left to read it here.
      </div>
    )
  }
  if (node.restricted) {
    return (
      <div style={{ padding: 20, fontSize: 14, color: 'var(--color-warning, #b45309)' }}>
        {node.title} — you don’t have access to this step. Ask an administrator
        if you need it.
      </div>
    )
  }

  return (
    <div>
      {/* The record editor's own header, minus the things a read-only pane has
          no business offering (Save, Save & Close). */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, padding: '12px 18px', position: 'sticky', top: 0, zIndex: 1,
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <strong style={{
            fontSize: '0.9625rem', whiteSpace: 'nowrap', overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>{node.title}</strong>
          <FormModeBadge mode="view" lockReason={node.lock_reason} />
          {txnId && (
            <code style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
              background: 'var(--color-bg-secondary)', padding: '2px 6px',
              borderRadius: 3, whiteSpace: 'nowrap',
            }}>
              {txnId.slice(0, 18)}{txnId.length > 18 ? '…' : ''}
            </code>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          {primary.map((a) => (
            <button
              key={a.label}
              className="btn btn-ghost btn-sm"
              onClick={a.onClick}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              {a.icon} {a.label}
            </button>
          ))}
          {menu.length > 0 && (
            <RowActionsMenu actions={menu} zIndex={MENU_Z_OVER_MODAL} />
          )}
        </div>
      </div>

      <div style={{ padding: '0 12px' }}>
        {error && <div style={{ color: 'var(--color-error, #dc2626)', fontSize: 14, padding: 12 }}>{error}</div>}
        {!error && !values && (
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', padding: 12 }}>
            Loading…
          </div>
        )}
        {!error && values && headerForm && (
          <FormRenderer
            model={headerForm}
            values={values}
            errors={{}}
            onChange={() => { /* view only */ }}
            readOnlyKey
            viewOnly
            // The record is persisted and this is where people act on it, so
            // the object's own buttons are drawn — the same bar, gated the
            // same way, as on the object's screen.
            actionsInViewOnly
            lockReason={node.lock_reason}
            canUseAction={canUseAction}
            project={project}
            objName={node.obj_name}
            formNo={headerForm.form_no}
            txnId={txnId}
            onActionResult={(res) => onActionNote({
              // Nothing here can take a form's worth of changes back: this pane
              // has no editable form. The record's own screen does.
              text: `“${res.action || 'That action'}” continues on the `
                + `${node.title} screen — open the record there.`,
            })}
            onActionDone={(res) => {
              if (!res.handler || res.handler === 'message' || res.handler === 'system') {
                onActionNote({ text: res.message || 'Done' })
              }
              onActionSettled()
            }}
            onActionError={(err) => {
              const parsed = parseApiError(err)
              onActionNote({
                bad: true,
                text: parsed.validationErrors[0]?.message || parsed.message || 'That action failed',
              })
            }}
          />
        )}
        {!error && values && !headerForm && (
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', padding: 12 }}>
            This record opens on its own screen.
          </div>
        )}
      </div>
    </div>
  )
}
